import React, { useRef, useEffect, useState } from 'react';

// Optimized FlowerField Animation
export const FlowerField = () => {
  const canvasRef = useRef(null);
  const flowersRef = useRef([]);
  const frameRef = useRef(0);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    
    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    
    // Flower class
    class Flower {
      constructor() {
        this.petals = 5 + Math.floor(Math.random() * 3);
        this.size = 5 + Math.random() * 12;
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.speed = 0.2 + Math.random() * 0.5;
        this.hue = Math.random() * 60 + 280; // Purple/pink range
        this.rotation = Math.random() * Math.PI * 2;
        this.rotationSpeed = (Math.random() - 0.5) * 0.01;
      }
      
      update() {
        this.y -= this.speed;
        this.rotation += this.rotationSpeed;
        
        if (this.y < -this.size * 2) {
          this.y = canvas.height + this.size;
          this.x = Math.random() * canvas.width;
        }
      }
      
      draw() {
        ctx.save();
        ctx.translate(this.x, this.y);
        ctx.rotate(this.rotation);
        
        // Draw petals (optimized to use fewer operations)
        ctx.fillStyle = `hsla(${this.hue}, 70%, 60%, 0.2)`;
        for (let i = 0; i < this.petals; i++) {
          const angle = (i / this.petals) * Math.PI * 2;
          ctx.beginPath();
          ctx.ellipse(
            Math.cos(angle) * this.size, 
            Math.sin(angle) * this.size, 
            this.size, 
            this.size/2, 
            angle, 
            0, 
            Math.PI * 2
          );
          ctx.fill();
        }
        
        // Draw center
        ctx.beginPath();
        ctx.arc(0, 0, this.size/3, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(50, 80%, 60%, 0.3)`;
        ctx.fill();
        
        ctx.restore();
      }
    }
    
    // Create flowers (fewer for better performance)
    const flowerCount = Math.min(10, Math.floor(canvas.width * canvas.height / 50000));
    if (flowersRef.current.length === 0) {
      for (let i = 0; i < flowerCount; i++) {
        flowersRef.current.push(new Flower());
      }
    }
    
    // Optimized animation loop with frame skipping
    let lastTime = 0;
    const frameInterval = 1000 / 30; // Target 30fps
    
    const animate = (time) => {
      frameRef.current = requestAnimationFrame(animate);
      
      // Throttle frame rate
      if (time - lastTime < frameInterval) return;
      lastTime = time;
      
      // Clear with transparent background
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Update and draw flowers
      flowersRef.current.forEach(flower => {
        flower.update();
        flower.draw();
      });
    };
    
    frameRef.current = requestAnimationFrame(animate);
    
    return () => {
      cancelAnimationFrame(frameRef.current);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);
  
  return (
    <canvas 
      ref={canvasRef} 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        pointerEvents: 'none'
      }}
    />
  );
};

// Optimized GeometricShapes Animation
export const GeometricShapes = () => {
  const canvasRef = useRef(null);
  const shapesRef = useRef([]);
  const frameRef = useRef(0);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    
    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    
    // Shape class
    class Shape {
      constructor() {
        this.type = Math.floor(Math.random() * 3); // 0: triangle, 1: square, 2: circle
        this.size = 10 + Math.random() * 20;
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.speed = 0.3 + Math.random() * 0.7;
        this.hue = Math.random() * 360;
        this.rotation = Math.random() * Math.PI * 2;
        this.rotationSpeed = (Math.random() - 0.5) * 0.02;
      }
      
      update() {
        this.y -= this.speed;
        this.rotation += this.rotationSpeed;
        
        if (this.y < -this.size * 2) {
          this.y = canvas.height + this.size;
          this.x = Math.random() * canvas.width;
        }
      }
      
      draw() {
        ctx.save();
        ctx.translate(this.x, this.y);
        ctx.rotate(this.rotation);
        
        ctx.fillStyle = `hsla(${this.hue}, 70%, 60%, 0.15)`;
        ctx.strokeStyle = `hsla(${this.hue}, 70%, 70%, 0.3)`;
        ctx.lineWidth = 1;
        
        if (this.type === 0) {
          // Triangle
          ctx.beginPath();
          ctx.moveTo(0, -this.size);
          ctx.lineTo(-this.size, this.size);
          ctx.lineTo(this.size, this.size);
          ctx.closePath();
        } else if (this.type === 1) {
          // Square
          ctx.beginPath();
          ctx.rect(-this.size/2, -this.size/2, this.size, this.size);
        } else {
          // Circle
          ctx.beginPath();
          ctx.arc(0, 0, this.size/2, 0, Math.PI * 2);
        }
        
        ctx.fill();
        ctx.stroke();
        
        ctx.restore();
      }
    }
    
    // Create shapes (fewer for better performance)
    const shapeCount = Math.min(15, Math.floor(canvas.width * canvas.height / 40000));
    if (shapesRef.current.length === 0) {
      for (let i = 0; i < shapeCount; i++) {
        shapesRef.current.push(new Shape());
      }
    }
    
    // Optimized animation loop with frame skipping
    let lastTime = 0;
    const frameInterval = 1000 / 30; // Target 30fps
    
    const animate = (time) => {
      frameRef.current = requestAnimationFrame(animate);
      
      // Throttle frame rate
      if (time - lastTime < frameInterval) return;
      lastTime = time;
      
      // Clear with transparent background
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Update and draw shapes
      shapesRef.current.forEach(shape => {
        shape.update();
        shape.draw();
      });
    };
    
    frameRef.current = requestAnimationFrame(animate);
    
    return () => {
      cancelAnimationFrame(frameRef.current);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);
  
  return (
    <canvas 
      ref={canvasRef} 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        pointerEvents: 'none'
      }}
    />
  );
};

// Optimized ParticleNetwork Animation
export const ParticleNetwork = () => {
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const frameRef = useRef(0);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    
    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    
    // Particle class
    class Particle {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = 1 + Math.random() * 3;
        this.speedX = (Math.random() - 0.5) * 0.5;
        this.speedY = (Math.random() - 0.5) * 0.5;
        this.hue = Math.random() * 360;
      }
      
      update() {
        this.x += this.speedX;
        this.y += this.speedY;
        
        // Bounce off edges
        if (this.x < 0 || this.x > canvas.width) this.speedX *= -1;
        if (this.y < 0 || this.y > canvas.height) this.speedY *= -1;
      }
      
      draw() {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${this.hue}, 70%, 60%, 0.7)`;
        ctx.fill();
      }
    }
    
    // Create particles (fewer for better performance)
    const particleCount = Math.min(30, Math.floor(canvas.width * canvas.height / 8000));
    if (particlesRef.current.length === 0) {
      for (let i = 0; i < particleCount; i++) {
        particlesRef.current.push(new Particle());
      }
    }
    
    // Draw connections between particles (optimized with distance check)
    const drawConnections = () => {
      const particles = particlesRef.current;
      
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const distanceSquared = dx * dx + dy * dy;
          
          // Skip square root calculation for performance
          if (distanceSquared < 10000) { // 100^2 = 10000
            const distance = Math.sqrt(distanceSquared);
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `hsla(${(particles[i].hue + particles[j].hue) / 2}, 70%, 60%, ${0.2 * (1 - distance/100)})`;
            ctx.lineWidth = 1.5;
            ctx.stroke();
          }
        }
      }
    };
    
    // Optimized animation loop with frame skipping
    let lastTime = 0;
    const frameInterval = 1000 / 25; // Target 25fps for this heavier animation
    
    const animate = (time) => {
      frameRef.current = requestAnimationFrame(animate);
      
      // Throttle frame rate
      if (time - lastTime < frameInterval) return;
      lastTime = time;
      
      // Clear with transparent background
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Update and draw particles
      particlesRef.current.forEach(particle => {
        particle.update();
        particle.draw();
      });
      
      // Draw connections
      drawConnections();
    };
    
    frameRef.current = requestAnimationFrame(animate);
    
    return () => {
      cancelAnimationFrame(frameRef.current);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);
  
  return (
    <canvas 
      ref={canvasRef} 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        pointerEvents: 'none'
      }}
    />
  );
};

// Optimized CodeSymbols Animation
export const CodeSymbols = () => {
  const canvasRef = useRef(null);
  const symbolsRef = useRef([]);
  const frameRef = useRef(0);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    
    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    
    // Programming symbols
    const symbols = ['{', '}', '<', '>', ';', '=', '(', ')', '[', ']', '*', '+', '-', '%'];
    
    // Symbol class
    class Symbol {
      constructor() {
        this.char = symbols[Math.floor(Math.random() * symbols.length)];
        this.size = 10 + Math.random() * 30;
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.speed = 0.3 + Math.random() * 0.7;
        this.hue = Math.random() * 60 + 180; // Blue-green range
        this.rotation = Math.random() * Math.PI * 2;
        this.rotationSpeed = (Math.random() - 0.5) * 0.01;
      }
      
      update() {
        this.y -= this.speed;
        this.rotation += this.rotationSpeed;
        
        if (this.y < -this.size * 2) {
          this.y = canvas.height + this.size;
          this.x = Math.random() * canvas.width;
        }
      }
      
      draw() {
        ctx.save();
        ctx.translate(this.x, this.y);
        ctx.rotate(this.rotation);
        
        ctx.font = `${this.size}px 'Fira Code', monospace`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = `hsla(${this.hue}, 70%, 60%, 0.2)`;
        ctx.fillText(this.char, 0, 0);
        
        ctx.restore();
      }
    }
    
    // Create symbols (fewer for better performance)
    const symbolCount = Math.min(15, Math.floor(canvas.width * canvas.height / 40000));
    if (symbolsRef.current.length === 0) {
      for (let i = 0; i < symbolCount; i++) {
        symbolsRef.current.push(new Symbol());
      }
    }
    
    // Optimized animation loop with frame skipping
    let lastTime = 0;
    const frameInterval = 1000 / 30; // Target 30fps
    
    const animate = (time) => {
      frameRef.current = requestAnimationFrame(animate);
      
      // Throttle frame rate
      if (time - lastTime < frameInterval) return;
      lastTime = time;
      
      // Clear with transparent background
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Update and draw symbols
      symbolsRef.current.forEach(symbol => {
        symbol.update();
        symbol.draw();
      });
    };
    
    frameRef.current = requestAnimationFrame(animate);
    
    return () => {
      cancelAnimationFrame(frameRef.current);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);
  
  return (
    <canvas 
      ref={canvasRef} 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        pointerEvents: 'none'
      }}
    />
  );
};

// Ultra Lightweight Animation (Simplest option for low-end PCs)
export const SimpleDots = () => {
  const canvasRef = useRef(null);
  const dotsRef = useRef([]);
  const frameRef = useRef(0);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    
    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    
    // Dot class
    class Dot {
      constructor() {
        this.size = 1 + Math.random() * 3;
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.speed = 0.2 + Math.random() * 0.5;
        this.hue = Math.random() * 360;
      }
      
      update() {
        this.y -= this.speed;
        
        if (this.y < -this.size * 2) {
          this.y = canvas.height + this.size;
          this.x = Math.random() * canvas.width;
        }
      }
      
      draw() {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${this.hue}, 70%, 60%, 0.3)`;
        ctx.fill();
      }
    }
    
    // Create dots (very few for maximum performance)
    const dotCount = Math.min(20, Math.floor(canvas.width * canvas.height / 60000));
    if (dotsRef.current.length === 0) {
      for (let i = 0; i < dotCount; i++) {
        dotsRef.current.push(new Dot());
      }
    }
    
    // Optimized animation loop with frame skipping
    let lastTime = 0;
    const frameInterval = 1000 / 30; // Target 30fps
    
    const animate = (time) => {
      frameRef.current = requestAnimationFrame(animate);
      
      // Throttle frame rate
      if (time - lastTime < frameInterval) return;
      lastTime = time;
      
      // Clear with transparent background
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Update and draw dots
      dotsRef.current.forEach(dot => {
        dot.update();
        dot.draw();
      });
    };
    
    frameRef.current = requestAnimationFrame(animate);
    
    return () => {
      cancelAnimationFrame(frameRef.current);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);
  
  return (
    <canvas 
      ref={canvasRef} 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        pointerEvents: 'none'
      }}
    />
  );
};

// Animation Selector Component
export const BackgroundAnimation = ({ type = 'simple' }) => {
  switch (type) {
    case 'flowers':
      return <FlowerField />;
    case 'shapes':
      return <GeometricShapes />;
    case 'particles':
      return <ParticleNetwork />;
    case 'code':
      return <CodeSymbols />;
    case 'simple':
      return <SimpleDots />;
    case 'none':
    default:
      return null;
  }
};

// Settings panel integration for your NinjaType component
export const AnimationSettings = ({ animationType, setAnimationType, showSettings }) => {
  if (!showSettings) return null;
  
  const animationOptions = [
    { id: 'simple', name: 'Simple Dots (Lightest)' },
    { id: 'flowers', name: 'Floating Flowers' },
    { id: 'shapes', name: 'Geometric Shapes' },
    { id: 'particles', name: 'Particle Network' },
    { id: 'code', name: 'Code Symbols' },
    { id: 'none', name: 'No Animation' }
  ];
  
  return (
    <div className="settings-group">
      <h4>Background Animation</h4>
      <div className="setting-group">
        <label>Animation Type:</label>
        <select
          value={animationType}
          onChange={(e) => setAnimationType(e.target.value)}
        >
          {animationOptions.map(option => (
            <option key={option.id} value={option.id}>{option.name}</option>
          ))}
        </select>
      </div>
      <p className="setting-description">
        Choose "Simple Dots" or "No Animation" for better performance on low-end devices.
      </p>
    </div>
  );
};

export default FlowerField;